import { useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAnchorProgress } from '@bprogress/react';

import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { AppSidebar } from './navbar';
import { Header } from './header';
import { AppFooter } from './footer';
import { SidebarAutoCollapse } from './sidebar-auto-collapse';
import { useApplyBranding } from '@/features/agency/hooks/use-apply-branding';
import { useCurrentOrganization } from '@/features/organization/hooks/use-current-organization';
import { CommandCenter } from '@/features/command-center';
// Imported from its own file, not the `@/features/voice` barrel — the barrel
// also re-exports the lazy-loaded settings/call-history pages, and a static
// import through it would pull those into the main bundle instead of their
// own route chunks.
import { CallWidget } from '@/features/voice/components/call-widget/call-widget';
// Same reasoning as CallWidget above — imported from its own file so the
// contacts barrel's lazy route pages don't get pulled into the main bundle.
// The component itself renders nothing until a composer is open, and lazy-loads
// the window (and with it the rich-text editor) only then.
import { ContactComposerMount } from '@/features/contacts/components/contact-composer/contact-composer-mount';
import { BulkJobToastsMount } from '@/features/contacts/components/bulk-job-toasts-mount';
import { GlobalUsageBanner } from '@/features/billing/components/global-usage-banner';
import { usePageHeaderStore } from '@/stores/page-header.store';

// Routes that opt out of the global `p-8 pt-6` content gutter. The inbox is
// a three-pane, edge-to-edge surface that owns its own scroll regions; the
// shared padding would push its right-edge contact panel off-axis. Settings
// is a two-pane surface whose nav rail must sit flush with the header and
// left edge — it applies its own gutter inside the content pane. The AI-agent
// builder (`/ai-agents/new` and `/ai-agents/:id`) is the same two-pane,
// full-height shape (handled below so the `/ai-agents` list stays padded).
// The email template builder (`/email-templates/:id/builder`) is the same
// full-height, self-scrolling shape as the AI-agent builder. So is the workflow
// builder (`/workflows/:id/builder`), whose React Flow canvas has no height of
// its own and is meant to meet the app chrome — both are anchored on `/builder$`
// so their list, `/new`, `/runs` and `/analytics` siblings stay padded.
const EDGE_TO_EDGE_PREFIXES = ['/inbox', '/settings'] as const;
const EMAIL_BUILDER_PATTERN = /^\/email-templates\/[^/]+\/builder$/;
const WORKFLOW_BUILDER_PATTERN = /^\/workflows\/[^/]+\/builder$/;
// The WhatsApp template editor (`/templates/whatsapp/new` and `/:id`) is the
// same full-height shape as `/segments/new`: its action bar spans the width
// flush above the app footer. `/templates/whatsapp` (the list) keeps the gutter.
const WHATSAPP_TEMPLATE_EDITOR_PATTERN = /^\/templates\/whatsapp\/[^/]+\/?$/;

// `/opportunities` is deliberately NOT here: the Deals page keeps the shared
// gutter (owner ruling, 2026-09-27).
//
// `/segments/new` is a two-pane, full-height surface whose
// preview rail meets the right edge and whose action bar spans the full width
// flush above the app footer. With the shared gutter the bar floats inset with
// a strip of page showing beneath it, which reads as a stray card rather than
// as the page's own chrome. Matched EXACTLY so `/segments` and
// `/segments/:segmentId` keep the gutter.
//
// The Create Campaign wizard is the same shape: a fixed header band, one
// scrolling body and an action bar that has to meet the pane edge, otherwise
// the bar floats above the gutter and the preview rail scrolls underneath it.
// Exact again — `/campaigns/new/design` is the email builder round trip and
// keeps its gutter. The Connect ad account wizard reuses that frame.
const EDGE_TO_EDGE_EXACT = ['/segments/new', '/campaigns/new', '/advertising/connect'] as const;

/** Trailing slashes are normalized so `/segments/new/` matches too. */
function isExactEdgeToEdge(pathname: string): boolean {
    const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    return EDGE_TO_EDGE_EXACT.some((p) => p === normalized);
}

// Routes that bring their own nav rail alongside their content pane, where the
// app sidebar's 16rem would put two rails side by side and squeeze the page.
// Collapse it to the 3rem icon rail for the duration of the visit — see
// `SidebarAutoCollapse` for how the previous state is handed back, and for why
// a manual toggle while you are here still wins.
//
// Settings has a nav rail of its own; the inbox has the filter rail plus three
// panes after it, which is the tightest horizontal budget in the app.
const SIDEBAR_COLLAPSE_PREFIXES = ['/settings', '/inbox'] as const;

function NavigationProgress() {
    useAnchorProgress({ startOnLoad: false });
    return null;
}

function AppLayout() {
    const { data: org } = useCurrentOrganization();
    // §51 white-label: brand color scale, favicon and tab title.
    useApplyBranding();
    const { pathname } = useLocation();
    const isEdgeToEdge =
        EDGE_TO_EDGE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`)) ||
        isExactEdgeToEdge(pathname) ||
        // builder (new / :id) only — the `/ai-agents` list stays padded
        pathname.startsWith('/ai-agents/') ||
        EMAIL_BUILDER_PATTERN.test(pathname) ||
        WORKFLOW_BUILDER_PATTERN.test(pathname) ||
        WHATSAPP_TEMPLATE_EDITOR_PATTERN.test(pathname);
    const isSidebarCollapseRoute = SIDEBAR_COLLAPSE_PREFIXES.some(
        (p) => pathname === p || pathname.startsWith(`${p}/`),
    );

    // The window never scrolls (`h-svh overflow-hidden` above, plus an
    // `overflow-hidden` SidebarInset) — this div is the app's only scroll
    // container, so its offset otherwise survives every navigation. Scroll a
    // list, click into a detail page, and the new page mounts already scrolled.
    // React Router's <ScrollRestoration> can't help: it only manages window
    // scroll, which is pinned here.
    const scrollRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        // Layout effect, not a plain effect, so the reset lands before paint
        // and the user never sees the wrong offset flash.
        scrollRef.current?.scrollTo({ top: 0, left: 0 });
    }, [pathname]);

    // Page-supplied top-bar title + badge (e.g. Daily Updates' title).
    const pageTitle = usePageHeaderStore((s) => s.title);
    const pageBadge = usePageHeaderStore((s) => s.badge);
    const headerLeft = pageTitle ? (
        <div className="flex items-center gap-2">
            <span className="text-2xl font-bold tracking-tight text-text-heading">{pageTitle}</span>
            {pageBadge ? (
                <Badge variant="default" className="rounded-full px-2.5 py-0.5 text-xs font-bold">
                    {pageBadge}
                </Badge>
            ) : null}
        </div>
    ) : undefined;

    return (
        <SidebarProvider className="h-svh overflow-hidden">
            <NavigationProgress />
            <CommandCenter orgId={org?.id ?? ''} />
            <SidebarAutoCollapse shouldCollapse={isSidebarCollapseRoute} />
            <AppSidebar />
            <SidebarInset className="min-w-0 overflow-hidden">
                <Header leftContent={headerLeft} />
                <GlobalUsageBanner />
                <div
                    ref={scrollRef}
                    data-slot="app-scroll-container"
                    className={cn(
                        'min-w-0 flex-1 overflow-y-auto overflow-x-hidden',
                        !isEdgeToEdge && 'p-8 pt-6',
                    )}
                >
                    <Outlet />
                </div>
                <AppFooter />
            </SidebarInset>
            <CallWidget />
            <ContactComposerMount />
            <BulkJobToastsMount />
        </SidebarProvider>
    );
}

export { AppLayout };
